import * as React from 'react';
// Import material ui modules
import {
	Calculate,
} from '@mui/icons-material';
// Import hooks.
import {
    useInputButton,
    UseInputButtonResult,
    useInputCurrency,
    UseInputCurrencyResults,
    useInputDataSelectionList,
    UseInputDataSelectionListResults,
    UseInputFormResult,
} from '@app/hooks/view/input';
// Import api models.
import {
	BuildingLimitsPersonal,
    BuildingLimitsPersonalWithRcv,
    PossibleBaseData,
} from '@app/models/api';
// Import enumerations.
import {
	AppThemeColor,
	ButtonSize,
	PersonalPropertyLossAdjustmentBases,
} from '@app/enumerations';
// Import props.
import {
	FormInputSettingsCoverageLimitsAndDeductiblesProps,
	FormInputSettingsCoverageLimitsAndDeductiblesPersonalProps,
} from '@app/props';
// Import app models.
import {
	ValidatorResponse,
} from '@app/models/app';

interface UseSubformCoverageLimitsPersonalParameters {
    id: string;
    allowEdits: boolean;
	showValidations?: boolean;
	inputSettingsProps?: Omit<FormInputSettingsCoverageLimitsAndDeductiblesPersonalProps, keyof FormInputSettingsCoverageLimitsAndDeductiblesProps>
	buildingLimitsPersonal?: BuildingLimitsPersonalWithRcv;
	onCalculatePremium?: () => void;
}

interface UseSubformCoverageLimitsPersonalResults {
	areAllInputsDisabled: boolean;
	showBuildingLimitInput: boolean;
	showContentsLimitInput: boolean;
	showPersonalPropertyLossAdjustmentBasesInput: boolean;
	showOtherStructuresLimitInput: boolean;
	showAdditionalLivingExpenseLimitInput: boolean;
	showCoverageLimits: boolean;
	showCalculatePremiumButton: boolean;
	calculatePremiumButton: UseInputButtonResult;
	buildingLimitInput: UseInputCurrencyResults;
	contentsLimitInput: UseInputCurrencyResults;
	personalPropertyLossAdjustmentBasesInput: UseInputDataSelectionListResults;
	otherStructuresLimitInput: UseInputCurrencyResults;
	additionalLivingExpenseLimitInput: UseInputCurrencyResults;
	createBuildingLimits: () => BuildingLimitsPersonal;
    checkFormValidation: (focusToElement: boolean) => boolean;
    flagAllInputsToShowValidations: () => void;
    doesFormHaveChanges: () => boolean;
}

const useSubformCoverageLimitsPersonal = (params: UseSubformCoverageLimitsPersonalParameters): UseSubformCoverageLimitsPersonalResults => {

    // #region exported methods

	const createBuildingLimits = (): BuildingLimitsPersonal => {
		let buildingLimits: BuildingLimitsPersonal = {
			building: buildingLimitInput.value ?? 0,
			contents: contentsLimitInput.value ?? 0,
			contentsUseReplacementCostValue: personalPropertyLossAdjustmentBasesInput.value === PersonalPropertyLossAdjustmentBases.RCV,
			otherStructures: otherStructuresLimitInput.value ?? 0,
			additionalLivingExpense: additionalLivingExpenseLimitInput.value ?? 0,
		};

		return buildingLimits;
	}

    /**
     * Method to check form validation with focus.
     **/
    const checkFormValidation = (focusToElement: boolean): boolean => {
		return checkInputValidation<number | undefined>(buildingLimitInput, focusToElement)
			&& checkInputValidation<number | undefined>(contentsLimitInput, focusToElement)
			&& checkInputValidation<string | undefined>(personalPropertyLossAdjustmentBasesInput, focusToElement)
			&& checkInputValidation<number | undefined>(otherStructuresLimitInput, focusToElement)
			&& checkInputValidation<number | undefined>(additionalLivingExpenseLimitInput, focusToElement);
    };

    /**
     * Flags all inputs to show the validation messages.
     **/
    const flagAllInputsToShowValidations = (): void => {
		buildingLimitInput.changeShowValidationFlag(true);
		contentsLimitInput.changeShowValidationFlag(true);
		personalPropertyLossAdjustmentBasesInput.changeShowValidationFlag(true);
		otherStructuresLimitInput.changeShowValidationFlag(true);
		additionalLivingExpenseLimitInput.changeShowValidationFlag(true);
    };

    /**
     * Checks to see if the form has changes. 
     **/
    const doesFormHaveChanges = (): boolean => {
		return buildingLimitInput.doesInputHaveChanges
			|| contentsLimitInput.doesInputHaveChanges
			|| personalPropertyLossAdjustmentBasesInput.doesInputHaveChanges
			|| otherStructuresLimitInput.doesInputHaveChanges
			|| additionalLivingExpenseLimitInput.doesInputHaveChanges;
    }

    // #endregion exported methods

	// #region private methods

	const validateAleLimitAgainstBuildingLimit = (value: number | undefined, required: boolean): ValidatorResponse => {
		let response: ValidatorResponse = new ValidatorResponse(true, String.empty());

		if (value !== undefined && value > allowedALEMaxLimit) {
			response = new ValidatorResponse(false, "The additional living expense limit cannot be greater than 10% of the building limit or $25,000, which ever is smaller");
		}
		return response;
	};

	/**
	 * Validates an individual input.
	 * @param input
	 */
	const checkInputValidation = <T>(input: UseInputFormResult<T>, focusToElement: boolean): boolean => {
		let isValid = input.isInputValid;
		if (!isValid && focusToElement) {
			input.focusToInput();
		}
		return isValid;
	}

	const getPersonalPropertyLossAdjustmentBasesOptions = (): PossibleBaseData[] => {
		return [
			{
				code: PersonalPropertyLossAdjustmentBases.RCV,
				displayName: PersonalPropertyLossAdjustmentBases.RCV,
			},
			{
				code: PersonalPropertyLossAdjustmentBases.ACV,
				displayName: PersonalPropertyLossAdjustmentBases.ACV,
			}
		];
	}

    // #region id generators

	/**
	 * Generates base id. 
	 **/
	const generateBaseId = (): string => {
		return params.id;
	}

	/**
	 * Generates id for the calculate premium button. 
	 **/
	const generateIdForCalculatePremiumButton = (): string => {
		return `${generateBaseId()}-calculatePremium-button`;
	}

	/**
	 * Generates id for the building limit. 
	 **/
	const generateIdForBuildingLimit = (): string => {
		return `${generateBaseId()}-buildingLimit`;
	}

	/**
	 * Generates id for the contents limit. 
	 **/
	const generateIdForPersonalPropertyLimit = (): string => {
		return `${generateBaseId()}-personalPropertyLimit`;
	}

	/**
	 * Generates id for the personal property loss adjustment bases. 
	 **/
	const generateIdForPersonalPropertyLossAdjustmentBases = (): string => {
		return `${generateBaseId()}-personalPropertyLossAdjustmentBases`;
	}

	/**
	 * Generates id for the other structures limit. 
	 **/
	const generateIdForOtherStructuresLimit = (): string => {
		return `${generateBaseId()}-otherStructuresLimit`;
	}

	/**
	 * Generates id for the additional living expense limit. 
	 **/
	const generateIdForAdditionalLivingExpenseLimit = (): string => {
		return `${generateBaseId()}-additionalLivingExpenseLimit`;
	}

    // #endregion id generators

    // #endregion private methods

    // #region variables

	const showCoverageLimits: boolean = React.useMemo(() => params.buildingLimitsPersonal !== undefined, [params.buildingLimitsPersonal]);

	// #region Input settings visibility.

	const showBuildingLimitInput: boolean = React.useMemo(() =>
		params.inputSettingsProps?.buildingLimitInput !== 'hidden'
		&& showCoverageLimits,
		[
			params.inputSettingsProps?.buildingLimitInput,
			showCoverageLimits,
		]
	);
	const showContentsLimitInput: boolean = React.useMemo(() =>
		params.inputSettingsProps?.contentsLimitInput !== 'hidden'
		&& showCoverageLimits,
		[
			params.inputSettingsProps?.contentsLimitInput,
			showCoverageLimits,
		]
	);
	const showPersonalPropertyLossAdjustmentBasesInput: boolean = React.useMemo(() =>
		params.inputSettingsProps?.personalPropertyLossAdjustmentBasesInput !== 'hidden'
		&& showCoverageLimits,
		[
			params.inputSettingsProps?.personalPropertyLossAdjustmentBasesInput,
			showCoverageLimits,
		]
	);
	const showOtherStructuresLimitInput: boolean = React.useMemo(() =>
		params.inputSettingsProps?.otherStructuresLimitInput !== 'hidden'
		&& showCoverageLimits,
		[
			params.inputSettingsProps?.otherStructuresLimitInput,
			showCoverageLimits,
		]
	);
	const showAdditionalLivingExpenseLimitInput: boolean = React.useMemo(() =>
		params.inputSettingsProps?.additionalLivingExpenseLimitInput !== 'hidden'
		&& showCoverageLimits,
		[
			params.inputSettingsProps?.additionalLivingExpenseLimitInput,
			showCoverageLimits,
		]
	);

	// #endregion Input settings visibility.

	// #region Input settings disable.

	const isBuildingLimitInputDisabled: boolean = React.useMemo(() =>
		!params.allowEdits
		|| !showBuildingLimitInput
		|| params.inputSettingsProps?.buildingLimitInput === 'disabled',
		[
			params.allowEdits,
			showBuildingLimitInput,
			params.inputSettingsProps?.buildingLimitInput
		]
	);
	const isContentsLimitInputDisabled: boolean = React.useMemo(() =>
		!params.allowEdits
		|| !showContentsLimitInput
		|| params.inputSettingsProps?.contentsLimitInput === 'disabled',
		[
			params.allowEdits,
			showContentsLimitInput,
			params.inputSettingsProps?.contentsLimitInput
		]
	);
	const isPersonalPropertyLossAdjustmentBasesInputDisabled: boolean = React.useMemo(() =>
		!params.allowEdits
		|| !showPersonalPropertyLossAdjustmentBasesInput
		|| params.inputSettingsProps?.personalPropertyLossAdjustmentBasesInput === 'disabled',
		[
			params.allowEdits,
			showPersonalPropertyLossAdjustmentBasesInput,
			params.inputSettingsProps?.personalPropertyLossAdjustmentBasesInput
		]
	);
	const isOtherStructuresLimitInputDisabled: boolean = React.useMemo(() =>
		!params.allowEdits
		|| !showOtherStructuresLimitInput
		|| params.inputSettingsProps?.otherStructuresLimitInput === 'disabled',
		[
			params.allowEdits,
			showOtherStructuresLimitInput,
			params.inputSettingsProps?.otherStructuresLimitInput
		]
	);
	const isAdditionalLivingExpenseLimitInputDisabled: boolean = React.useMemo(() =>
		!params.allowEdits
		|| !showAdditionalLivingExpenseLimitInput
		|| params.inputSettingsProps?.additionalLivingExpenseLimitInput === 'disabled',
		[
			params.allowEdits,
			showAdditionalLivingExpenseLimitInput,
			params.inputSettingsProps?.additionalLivingExpenseLimitInput
		]
	);

	// #endregion Input settings disable.

	const showCalculatePremiumButton: boolean = React.useMemo(
		() => params.allowEdits && params.onCalculatePremium !== undefined,
		[params.allowEdits, params.onCalculatePremium]
	);

	// Setup the calculate premium button hook.
	const calculatePremiumButton: UseInputButtonResult = useInputButton({
		id: generateIdForCalculatePremiumButton(),
		size: ButtonSize.MEDIUM,
		buttonType: AppThemeColor.SECONDARY,
		startAdornmentIcon: Calculate,
		disabled: !showCalculatePremiumButton,
		label: 'Calculate Premium',
		onClick: params.onCalculatePremium,
	});

	// Setup the 'building limit' input hook.
	const buildingLimitInput: UseInputCurrencyResults = useInputCurrency({
		id: generateIdForBuildingLimit(),
		label: 'Building Limit',
		validationLabel: 'building limit',
		required: true,
		disabled: isBuildingLimitInputDisabled,
		value: params.buildingLimitsPersonal?.building ?? 0,
		minValue: 0,
		maxValue: params.buildingLimitsPersonal?.buildingReplacementCostValue ?? 0,
		maxValueInclusive: true,
		maxValueLabel: 'replacement cost value',
		showValidation: params.showValidations,
	});

	const allowedAleMaxLimitCappedAmount: number = 25000;
	const allowedALEMaxLimit: number = React.useMemo(() => {
		let maxLimit: number = (buildingLimitInput.value ?? 0) * .1;
		return Math.min(maxLimit, allowedAleMaxLimitCappedAmount);
	}, [buildingLimitInput.value]);

	// Setup the 'contents limit' input hook.
	const contentsLimitInput: UseInputCurrencyResults = useInputCurrency({
		id: generateIdForPersonalPropertyLimit(),
		label: 'Personal Property Limit',
		validationLabel: 'contents limit',
		required: true,
		disabled: isContentsLimitInputDisabled,
		value: params.buildingLimitsPersonal?.contents ?? 0,
		minValue: 0,
		minValueInclusive: true,
		showValidation: params.showValidations,
	});

	// Setup the 'person property loss adjustment bases' input hook.
	const personalPropertyLossAdjustmentBasesInput: UseInputDataSelectionListResults = useInputDataSelectionList({
		id: generateIdForPersonalPropertyLossAdjustmentBases(),
		label: 'Personal Property Loss Adjustment Bases',
		required: true,
		disabled: isPersonalPropertyLossAdjustmentBasesInputDisabled,
		value: params.buildingLimitsPersonal?.contentsUseReplacementCostValue ? PersonalPropertyLossAdjustmentBases.RCV : PersonalPropertyLossAdjustmentBases.ACV,
		possibleDataSelections: getPersonalPropertyLossAdjustmentBasesOptions(),
		showValidation: params.showValidations,
	});

	// Setup the 'other structures limit' input hook.
	const otherStructuresLimitInput: UseInputCurrencyResults = useInputCurrency({
		id: generateIdForOtherStructuresLimit(),
		label: 'Appurtenant Structures Limit',
		validationLabel: 'structures limit',
		required: true,
		disabled: isOtherStructuresLimitInputDisabled,
		value: params.buildingLimitsPersonal?.otherStructures ?? 0,
		minValue: 0,
		minValueInclusive: true,
		showValidation: params.showValidations,
	});

	// Setup the 'additional living expense limit' input hook.
	const additionalLivingExpenseLimitInput: UseInputCurrencyResults = useInputCurrency({
		id: generateIdForAdditionalLivingExpenseLimit(),
		label: 'Additional Living Expense Limit',
		validationLabel: 'additional living expense limit',
		required: true,
		disabled: isAdditionalLivingExpenseLimitInputDisabled,
		value: params.buildingLimitsPersonal?.additionalLivingExpense ?? 0,
		minValue: 0,
		minValueInclusive: true,
		showValidation: params.showValidations,
		validators: [validateAleLimitAgainstBuildingLimit],
	});

	// Check to see if all inputs are disabled.
	const areAllInputsDisabled: boolean = React.useMemo(() => {
		return buildingLimitInput.isDisabled
			&& contentsLimitInput.isDisabled
			&& personalPropertyLossAdjustmentBasesInput.isDisabled
			&& otherStructuresLimitInput.isDisabled
			&& additionalLivingExpenseLimitInput.isDisabled;
	}, [
		buildingLimitInput.isDisabled,
		contentsLimitInput.isDisabled,
		personalPropertyLossAdjustmentBasesInput.isDisabled,
		otherStructuresLimitInput.isDisabled,
		additionalLivingExpenseLimitInput.isDisabled,
	]);

    // #endregion variables

	return {
		areAllInputsDisabled,
		showBuildingLimitInput,
		showContentsLimitInput,
		showPersonalPropertyLossAdjustmentBasesInput,
		showOtherStructuresLimitInput,
		showAdditionalLivingExpenseLimitInput,
		showCoverageLimits,
		showCalculatePremiumButton,
		calculatePremiumButton,
		buildingLimitInput,
		contentsLimitInput,
		personalPropertyLossAdjustmentBasesInput,
		otherStructuresLimitInput,
		additionalLivingExpenseLimitInput,
		createBuildingLimits,
		checkFormValidation,
		flagAllInputsToShowValidations,
		doesFormHaveChanges,
    };
};

// Export parameters.
export { UseSubformCoverageLimitsPersonalParameters };

// Export results.
export { UseSubformCoverageLimitsPersonalResults };

export default useSubformCoverageLimitsPersonal;
